import { TrackingDataListItem } from '@api'
import { getCurrency } from '@common/utils/currency.ts'
import type { ColumnDef } from '@tanstack/react-table'
import { Link } from 'react-router-dom'

import ArrowIcon from '@/assets/icons/icon-arrow.svg'
import InfoIcon from '@/assets/icons/icon-info.svg'
import { Button } from '@/components/ui/button.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { AddTableRowButton } from '@/modules/sales-editor/components/add-table-row-button.tsx'
import { TableCellEditor } from '@/modules/sales-editor/components/table-cell-editor.tsx'
import { TeamAvatar } from '@/modules/team/ui/team-avatar.tsx'
import { formatSeconds } from '@/utils/helpers'
import { formatHour } from '@/utils/time.ts'

import type {
  AllChatterTableType,
  ChatterTrackingEntryWithDateType,
} from '../../service/chatter-tracking-service.ts'
import { ChatterTrackingActivityStatus } from '../ChatterTrackingActivityStatus.tsx'

export const chatterTableColumnTitles = {
  date: 'Date',
  name: 'Name',
  minutesActive: 'Active Time',
  minutesInactive: 'Inactive Time',
  totalNetRevenue: 'Total Revenue',
  averageResponseTime: 'Avg Response Time',
  sendMessages: 'Sent Messages',
  sentPPVs: 'Sent PPVs',
  boughtPPVs: 'Bought PPVs',
  action: 'Action',
} as const

const getDefaultColumns = (
  showCents = false,
): ColumnDef<
  AllChatterTableType | ChatterTrackingEntryWithDateType | undefined
>[] => {
  return [
    {
      accessorKey: 'minutesActive',
      header: () => {
        return (
          <Tooltip
            content="The time during which the chatter was typing or clicking while they were online"
            className="flex items-center justify-center gap-1"
          >
            {chatterTableColumnTitles.minutesActive}
            <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
          </Tooltip>
        )
      },
      cell: (ctx) => (
        <TableCellEditor
          cellContext={ctx}
          format={(value) => formatHour(Number(value))}
        />
      ),
    },
    {
      accessorKey: 'minutesInactive',
      header: () => {
        return (
          <Tooltip
            content="The time during which the chatter was inactive while they were online"
            className="flex items-center justify-center gap-1"
          >
            {chatterTableColumnTitles.minutesInactive}
            <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
          </Tooltip>
        )
      },
      cell: (ctx) => (
        <TableCellEditor
          cellContext={ctx}
          format={(value) => formatHour(Number(value))}
        />
      ),
    },
    {
      accessorKey: 'totalNetRevenue',
      header: () => {
        return (
          <Tooltip
            content="Shows the total revenue the chatter has acquired during the selected timeframe, including all messages and tips"
            className="flex items-center justify-center gap-1"
          >
            {chatterTableColumnTitles.totalNetRevenue}
            <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
          </Tooltip>
        )
      },
      cell: (ctx) => (
        <TableCellEditor
          cellContext={ctx}
          format={(value) => getCurrency(Number(value), showCents ? 2 : 0)}
        />
      ),
    },
    {
      accessorKey: 'averageResponseTime',
      header: () => {
        return (
          <Tooltip
            content="Shows the average repsonse time the chatter needs to reply per messages (max. 60 minutes) during the selected timeframe"
            className="flex items-center justify-center gap-1"
          >
            {chatterTableColumnTitles.averageResponseTime}
            <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
          </Tooltip>
        )
      },
      cell: (ctx) => (
        <TableCellEditor
          cellContext={ctx}
          format={(value) => formatSeconds(Number(value))}
        />
      ),
    },
    {
      accessorKey: 'sendMessages',
      header: () => {
        return (
          <Tooltip
            content="Shows the total amount of messages the chatter has sent out during the selected timeframe"
            className="flex items-center justify-center gap-1"
          >
            {chatterTableColumnTitles.sendMessages}
            <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
          </Tooltip>
        )
      },
      cell: (ctx) => <TableCellEditor cellContext={ctx} />,
    },
    {
      accessorKey: 'sentPPVs',
      header: () => {
        return (
          <Tooltip
            content="Shows the number of Pay-Per-Views the chatter has sent out during the selected timeframe"
            className="flex items-center justify-center gap-1"
          >
            {chatterTableColumnTitles.sentPPVs}
            <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
          </Tooltip>
        )
      },
      cell: (ctx) => <TableCellEditor cellContext={ctx} />,
    },
    {
      accessorKey: 'boughtPPVs',
      header: () => {
        return (
          <Tooltip
            content="Shows the number of Pay-Per-Views the chatter has sent out that were bought by a subscriber during the selected timeframe"
            className="flex items-center justify-center gap-1"
          >
            {chatterTableColumnTitles.boughtPPVs}
            <InfoIcon className="h-4 w-4 min-w-4 min-h-4" />
          </Tooltip>
        )
      },
      cell: (ctx) => <TableCellEditor cellContext={ctx} />,
    },
  ]
}

const dateFormat = new Intl.DateTimeFormat('en-US', {
  month: 'numeric',
  day: 'numeric',
  year: 'numeric',
})

const dateFormatWithTime = new Intl.DateTimeFormat('en-US', {
  month: 'numeric',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
})

export function getChatterColumns(
  firstColumn: 'name' | 'date',
  to?: string,
  showCents = false,
  showTime = false,
  activityStates?: TrackingDataListItem[],
): ColumnDef<
  AllChatterTableType | ChatterTrackingEntryWithDateType | undefined
>[] {
  const link = to ? `${to}/` : ''

  if (firstColumn === 'date') {
    return [
      {
        accessorKey: 'date',
        header: chatterTableColumnTitles.date,
        cell: ({ row }) => {
          const date = new Date(row.getValue('date'))

          if (showTime) return dateFormatWithTime.format(date)

          return dateFormat.format(date)
        },
      },
      ...getDefaultColumns(showCents),
    ]
  }

  function getAllChatterTableType(
    original:
      | AllChatterTableType
      | ChatterTrackingEntryWithDateType
      | undefined,
  ) {
    return original ? (original as AllChatterTableType) : undefined
  }

  function getUserId(
    original:
      | AllChatterTableType
      | ChatterTrackingEntryWithDateType
      | undefined,
  ) {
    return getAllChatterTableType(original)?.userId
  }

  return [
    {
      accessorKey: 'name',
      header: () => (
        <div className="relative">
          {chatterTableColumnTitles.name}
          <AddTableRowButton />
        </div>
      ),
      cell: (ctx) => {
        const { row, cell } = ctx
        const name = cell.getValue<string>()
        const allChatterTableType = getAllChatterTableType(row.original)!
        const userId = allChatterTableType?.userId

        return (
          <div className="inline-flex w-fit items-center justify-center px-0">
            <Link
              to={`${link}${
                getUserId(row.original)
                  ? `user/${userId}`
                  : `browser/${encodeURI(String(name).replace('/', '[slash]'))}`
              }`}
              className="flex items-center gap-3 hover:text-primary-500 hover:no-underline"
            >
              <div className="relative">
                <TeamAvatar
                  name={name}
                  avatarSrc={allChatterTableType?.profilePicture}
                  className="h-10 w-10"
                />
                <ChatterTrackingActivityStatus
                  id={userId!}
                  activityStates={activityStates || []}
                />
              </div>
              <span className="truncate">
                <TableCellEditor
                  className="min-w-[150px]"
                  cellContext={ctx}
                />
              </span>
            </Link>
          </div>
        )
      },
    },
    ...getDefaultColumns(showCents),
    {
      accessorKey: 'action',
      header: chatterTableColumnTitles.action,
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          variant="link"
          className="w-fit px-0 text-primary-500 hover:no-underline"
        >
          <Link
            to={`${link}${
              getUserId(row.original)
                ? `user/${getUserId(row.original)}`
                : `browser/${encodeURI(
                    String(row.getValue('name')).replace('/', '[slash]'),
                  )}`
            }`}
          >
            View Stats
          </Link>
          <ArrowIcon />
        </Button>
      ),
    },
  ]
}

export const getAllChattersColumns = (
  showCents = false,
  showTime = false,
  activityStates?: TrackingDataListItem[],
): ColumnDef<
  AllChatterTableType | ChatterTrackingEntryWithDateType | undefined
>[] => getChatterColumns('name', undefined, showCents, showTime, activityStates)

export const getLeaderboardChattersColumns = (
  showCents = false,
  showTime = false,
  activityStates?: TrackingDataListItem[],
): ColumnDef<
  AllChatterTableType | ChatterTrackingEntryWithDateType | undefined
>[] => {
  return getChatterColumns(
    'name',
    '/overview/chatter-leaderboard',
    showCents,
    showTime,
    activityStates,
  )
}

export const getDetailedChatterLeaderboardColumns = (
  showTime = false,
): ColumnDef<
  AllChatterTableType | ChatterTrackingEntryWithDateType | undefined
>[] => getChatterColumns('date', undefined, false, showTime)
